// The clerk at the counter: the panel you talk to, the arrow that walks you to the bin,
// and the clerk himself turning to look at you.
//
// The answers come from clerkBrain.js, which is local and instant. If VITE_CLERK_URL is set
// the question goes there first, to a real language model, and the brain is the fallback
// when that is slow, down or says something unusable. The contract for that endpoint:
//
//   POST VITE_CLERK_URL   { question: string, history: [{ role: 'user'|'clerk', text }] }
//   200                   { reply: string, picks?: [recordId, ...] }
//
// Pick ids must be catalogue ids; unknown ones are dropped, so a model cannot send you to
// a record that does not exist. The key for any model belongs on that server, never here:
// everything in this file ships to the browser.
//
// Everything typed or returned is put on the page with textContent, never as HTML.

import * as THREE from 'three';
import { createClerkBrain } from './clerkBrain.js';
import { findPath } from './silhouettes.js';
import { RECORDS_BY_GENRE, GENRES } from './catalogue.js';

const TALK_RANGE = 3.2;            // metres from the kiosk centre
const GREET_RANGE = 3.6;           // the clerk looks up and greets you inside this
const LOOK_RANGE = 7;              // and turns to watch you inside this
const ARRIVE_RANGE = 1.5;          // close enough to the bin to count as arrived
const REMOTE_TIMEOUT_MS = 4000;
const STEP_M = 0.75;               // a step, for "about 12 steps from the till"

const ROOM_NAMES = {
  'entry-hall': 'the entry hall', 'reggae-room': 'the reggae room', 'listening-room': 'the listening room',
  'jazz-room': 'the jazz room', 'machine-room': 'the machine room',
};

const CSS = `
#clerk-panel{position:fixed;left:2rem;bottom:5.2rem;width:27rem;max-width:calc(100vw - 2rem);max-height:62vh;display:none;flex-direction:column;
  background:linear-gradient(178deg,rgba(32,25,17,.97),rgba(14,11,7,.97));border:1px solid rgba(214,188,140,.34);border-radius:3px;
  box-shadow:0 18px 60px rgba(0,0,0,.62),inset 0 0 0 1px rgba(214,188,140,.07);color:#efe6d4;
  font:400 13px/1.45 ui-sans-serif,system-ui,sans-serif;z-index:9;text-shadow:0 1px 2px rgba(0,0,0,.55);pointer-events:auto}
#clerk-panel.open{display:flex}
#clerk-panel header{display:flex;justify-content:space-between;align-items:baseline;padding:.65rem .9rem .5rem;border-bottom:1px solid rgba(214,188,140,.18)}
#clerk-panel header b{font:600 11px ui-monospace,monospace;letter-spacing:.16em;color:#d6bc8c}
#clerk-panel header span{font-size:11px;color:#9c8c70}
#clerk-log{overflow-y:auto;padding:.6rem .9rem;display:flex;flex-direction:column;gap:.55rem;min-height:5rem}
#clerk-log .you{align-self:flex-end;background:rgba(214,188,140,.14);padding:.3rem .6rem;border-radius:10px 10px 2px 10px;max-width:85%}
#clerk-log .them{white-space:pre-wrap}
#clerk-log .pick{border:1px solid rgba(214,188,140,.22);border-radius:3px;padding:.4rem .55rem;background:rgba(0,0,0,.22)}
#clerk-log .pick .t{font-weight:600}#clerk-log .pick .m{color:#b9a98c;font-size:12px}#clerk-log .pick .w{color:#8fb8c4;font-size:12px;margin-top:.1rem}
#clerk-log .pick button{margin-top:.35rem;background:rgba(214,188,140,.16);color:#efe6d4;border:1px solid rgba(214,188,140,.4);border-radius:2px;padding:.2rem .6rem;font:inherit;font-size:12px;cursor:pointer}
#clerk-log .pick button:hover{background:rgba(214,188,140,.3)}
#clerk-chips{display:flex;flex-wrap:wrap;gap:.35rem;padding:0 .9rem .5rem}
#clerk-chips button{background:transparent;color:#cdb98f;border:1px solid rgba(214,188,140,.3);border-radius:999px;padding:.12rem .6rem;font:inherit;font-size:12px;cursor:pointer}
#clerk-chips button:hover{background:rgba(214,188,140,.16)}
#clerk-form{display:flex;border-top:1px solid rgba(214,188,140,.18)}
#clerk-input{flex:1;background:transparent;border:0;outline:0;color:#efe6d4;font:inherit;padding:.65rem .9rem}
#clerk-input::placeholder{color:#7d7058}
#clerk-say{position:fixed;left:50%;bottom:calc(12% + 3rem);transform:translateX(-50%);max-width:34rem;padding:.45rem .9rem;border-radius:999px;
  background:rgba(12,10,8,.82);border:1px solid rgba(214,188,140,.22);color:#efe6d4;font:400 13px/1.4 ui-sans-serif,system-ui,sans-serif;
  opacity:0;transition:opacity .25s;pointer-events:none;z-index:8}
#clerk-say.show{opacity:1}
#clerk-guide{position:fixed;left:50%;top:13%;transform:translateX(-50%);display:none;align-items:center;gap:.6rem;padding:.35rem .8rem;
  border-radius:999px;background:rgba(12,10,8,.78);border:1px solid rgba(214,188,140,.3);color:#efe6d4;font:500 13px ui-sans-serif,system-ui,sans-serif;z-index:8;pointer-events:none}
#clerk-guide.on{display:flex}#clerk-guide svg{width:22px;height:22px;transition:transform .08s linear}
`;

function el(tag, props = {}, ...kids) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (k === 'class') node.className = v;
    else if (k === 'text') node.textContent = v;
    else if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
    else node.setAttribute(k, v);
  }
  for (const kid of kids) if (kid) node.append(kid);
  return node;
}

function createClerk({ scene, camera, player, digging, racks, alcoves, sfx, counter, roomHalfW }) {
  if (typeof document === 'undefined') return { update() {}, open() {}, close() {}, debug: () => ({ available: false }) };

  const records = Object.values(RECORDS_BY_GENRE).flat();
  const recordsById = new Map(records.map((r) => [r.id, r]));

  // the middle of the kiosk (COUNTER.position is the till spot, 0.1 m off the centre)
  const kiosk = { x: counter.position.x + 0.1, z: counter.position.z };
  const distToKiosk = () => Math.hypot(camera.position.x - kiosk.x, camera.position.z - kiosk.z);

  // ------------------------------------------------------------------ where things are
  const rackFor = (r) => racks.find((k) => k.genreId === r.genreId && k.subgenreId === r.subgenreId) || null;

  function locate(record) {
    const rack = rackFor(record);
    if (!rack || !rack.stand) return null;
    const { x, z } = rack.stand;
    let text;
    if (Math.abs(x) > roomHalfW + 0.05) {
      const room = alcoves.find((a) => a.alcove.side * x > 0 && Math.abs(z - a.alcove.z) <= a.alcove.d / 2 + 0.3);
      text = `in ${(room && ROOM_NAMES[room.id]) || 'one of the side rooms'}`;
    } else {
      const steps = Math.round(Math.hypot(x - kiosk.x, z - kiosk.z) / STEP_M);
      text = steps <= 3 ? 'right by the counter' : `out on the main floor, about ${steps} steps from the till`;
    }
    return { text, x, z, rack };
  }

  const brain = createClerkBrain({ records, genres: GENRES, locate, price: 125 });

  // ------------------------------------------------------------------------------- UI
  const style = el('style', { id: 'clerk-css' });
  style.textContent = CSS;
  document.head.append(style);

  const log = el('div', { id: 'clerk-log' });
  const chips = el('div', { id: 'clerk-chips' });
  const input = el('input', { id: 'clerk-input', type: 'text', maxlength: '200', autocomplete: 'off', spellcheck: 'false',
    placeholder: 'Ask for a mood, an artist, a decade…' });
  const form = el('form', { id: 'clerk-form' }, input);
  const panel = el('div', { id: 'clerk-panel', role: 'dialog', 'aria-label': 'Talk to the clerk' },
    el('header', {}, el('b', { text: 'THE CLERK' }), el('span', { text: 'NSPIRE RECORDS · Esc to close' })), log, chips, form);
  const say = el('div', { id: 'clerk-say' });
  const guide = el('div', { id: 'clerk-guide' });
  const arrow = el('span', {});
  arrow.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="#e6c98f" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M5 10l7-7 7 7"/></svg>';
  const guideText = el('span', {});
  guide.append(arrow, guideText);
  document.body.append(panel, say, guide);

  const history = [];
  let isOpen = false, revealing = 0, busy = false, greeted = false, welcomed = false, sayTimer = 0;
  const stats = { asked: 0, remote: 0, local: 0, guided: 0, arrived: 0 };

  function speak(text, seconds = 4.5) {
    say.textContent = `Clerk: ${text}`;
    say.classList.add('show');
    clearTimeout(sayTimer);
    sayTimer = setTimeout(() => say.classList.remove('show'), seconds * 1000);
  }

  function addYou(text) { log.append(el('div', { class: 'you', text })); log.scrollTop = log.scrollHeight; }

  // Text arrives a letter at a time with a soft blip, the way a person talks. Clicking the
  // log or pressing Enter finishes it at once. Each reveal owns its own timer and its own
  // finished flag: they used to share one, so the greeting finishing would cancel the timer of
  // an answer that had started while it was still typing, and that answer froze half-said.
  function reveal(node, full, done) {
    revealing++;
    let i = 0, lastBlip = 0, timer = 0, finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      node.textContent = full;
      revealing--;
      log.scrollTop = log.scrollHeight;
      done && done();
    };
    node._finish = finish;
    const tick = () => {
      if (finished) return;
      i = Math.min(full.length, i + 2);
      node.textContent = full.slice(0, i);
      const now = performance.now();
      if (/[a-z0-9]/i.test(full[i - 1] || '') && now - lastBlip > 70) { lastBlip = now; sfx && sfx.play('blip', { volume: 0.7 }); }
      log.scrollTop = log.scrollHeight;
      if (i >= full.length) finish(); else timer = setTimeout(tick, 24);
    };
    tick();
  }

  function addPick(pick) {
    const card = el('div', { class: 'pick' },
      el('div', { class: 't', text: `“${pick.title}”` }),
      el('div', { class: 'm', text: `${pick.artist} (${pick.year}) · ${pick.bin}` }),
      pick.place ? el('div', { class: 'w', text: pick.place.text }) : null);
    if (pick.place) card.append(el('button', { type: 'button', text: 'Take me there', onclick: () => guideTo(pick) }));
    log.append(card);
  }

  function setChips(list) {
    chips.replaceChildren(...list.slice(0, 5).map((text) => el('button', { type: 'button', text, onclick: () => submit(text) })));
  }

  async function askRemote(text) {
    const url = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CLERK_URL;
    if (!url) return null;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), REMOTE_TIMEOUT_MS);
    try {
      const res = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, signal: ctl.signal,
        body: JSON.stringify({ question: text, history: history.slice(-8) }) });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data || typeof data.reply !== 'string' || !data.reply.trim()) return null;
      const picks = (Array.isArray(data.picks) ? data.picks : []).map((id) => brain.pickFor(id)).filter(Boolean).slice(0, 3);
      return { intent: 'remote', reply: data.reply.slice(0, 1200), picks, suggestions: [] };
    } catch { return null; } finally { clearTimeout(timer); }
  }

  async function answer(text) {
    stats.asked++;
    const remote = await askRemote(text);
    if (remote) { stats.remote++; return remote; }
    stats.local++;
    await new Promise((r) => setTimeout(r, 280));            // a beat, so it reads as thinking
    return brain.ask(text);
  }

  async function submit(text) {
    text = String(text || '').trim();
    if (!text || busy) { if (revealing) log.querySelectorAll('.them').forEach((n) => n._finish && n._finish()); return; }
    busy = true;
    input.value = '';
    addYou(text);
    history.push({ role: 'user', text });
    const bubble = el('div', { class: 'them', text: '…' });
    log.append(bubble);
    log.scrollTop = log.scrollHeight;
    const result = await answer(text);
    history.push({ role: 'clerk', text: result.reply });
    reveal(bubble, result.reply, () => {
      (result.picks || []).forEach(addPick);
      if (result.suggestions && result.suggestions.length) setChips(result.suggestions);
      log.scrollTop = log.scrollHeight;
      busy = false;
    });
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    panel.classList.add('open');
    try { document.exitPointerLock && document.exitPointerLock(); } catch { /* not locked */ }
    player.keys && player.keys.clear();
    if (!greeted) {
      greeted = true;
      const g = brain.greet();
      const bubble = el('div', { class: 'them' });
      log.append(bubble);
      reveal(bubble, g.reply, () => setChips(g.suggestions));
    }
    input.focus();
    setTimeout(() => input.focus(), 0);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    panel.classList.remove('open');
    input.blur();
    log.querySelectorAll('.them').forEach((n) => n._finish && n._finish());
  }

  form.addEventListener('submit', (e) => { e.preventDefault(); submit(input.value); });

  // While the panel is open nothing typed may reach the player or the crates: W would walk,
  // E would pull a record, Q would put one back. This runs before their listeners.
  addEventListener('keydown', (e) => {
    if (!isOpen) return;
    if (e.code === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); close(); return; }
    if (e.target === input) return;             // typing: the box takes it (and hides it below)
    e.stopImmediatePropagation();               // anything else while the panel is up
  }, true);
  input.addEventListener('keydown', (e) => e.stopPropagation());

  // T, near the counter, opens the panel. (Not while typing in it: the capture listener
  // above has already swallowed those.)
  addEventListener('keydown', (e) => {
    if (e.code !== 'KeyT' || e.repeat || isOpen || e.ctrlKey || e.metaKey || e.altKey) return;
    if (digging.held || digging.rack) return;
    if (distToKiosk() <= TALK_RANGE) { e.preventDefault(); open(); }
    else speak('Come up to the counter and I’ll help you out.', 3);
  });

  // ------------------------------------------------------------------------- guidance
  let target = null, beacon = null, guideSince = 0;

  function makeBeacon() {
    const c = document.createElement('canvas');
    c.width = 8; c.height = 128;
    const g = c.getContext('2d');
    const grad = g.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0, 'rgba(255,214,150,0)'); grad.addColorStop(0.5, 'rgba(255,214,150,0.85)'); grad.addColorStop(1, 'rgba(255,214,150,0)');
    g.fillStyle = grad; g.fillRect(0, 0, 8, 128);
    const texture = new THREE.CanvasTexture(c);
    const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: true, opacity: 0.8 });
    const a = new THREE.PlaneGeometry(0.34, 2.1), b = new THREE.PlaneGeometry(0.34, 2.1);
    b.rotateY(Math.PI / 2);
    const mesh = new THREE.Mesh(a, material);
    mesh.add(new THREE.Mesh(b, material));
    mesh.renderOrder = 4;
    mesh.name = 'clerk-beacon';
    return mesh;
  }

  function guideTo(pick) {
    const place = pick.place || locate({ genreId: pick.genreId, subgenreId: pick.subgenreId });
    if (!place) return;
    cancelGuide();
    target = { x: place.x, z: place.z, rack: place.rack, label: pick.bin, genreId: pick.genreId };
    guideSince = performance.now();
    stats.guided++;
    close();
    if (startLeading(place)) {
      speak(`Right this way — ${pick.bin}.`, 4);
    } else {
      // no walkable route (or no clerk figure): fall back to the arrow and beacon
      beacon = makeBeacon();
      const p = place.rack.position || { x: place.x, z: place.z };
      beacon.position.set(p.x, 2.2, p.z);
      scene.add(beacon);
      guide.classList.add('on');
      speak(`Follow the arrow — ${pick.bin}.`, 4);
    }
    sfx && sfx.play('blip', { volume: 0.8 });
  }

  function cancelGuide() {
    if (beacon) {
      scene.remove(beacon);
      beacon.traverse((o) => { o.geometry && o.geometry.dispose(); });
      beacon.material.map && beacon.material.map.dispose();
      beacon.material.dispose();
      beacon = null;
    }
    target = null;
    guide.classList.remove('on');
    sendHome();
  }

  // ------------------------------------------------------------------------- the clerk walks you there
  // He leaves through the gap in the back of the kiosk, follows A* waypoints over the same
  // colliders the shoppers use, and waits for you if you fall behind.
  const LEAD_SPEED = 1.15, WAIT_GAP = 4.5, RESUME_GAP = 2.5, STAND_BACK = 0.9;
  const walk = { mode: 'desk', legs: [], leg: 0, home: null, homeYaw: 0, phase: 0, pause: 0 };
  const doorOut = { x: counter.position.x + 0.1, z: counter.position.z + 1.15 + 0.55 };

  function clerkFigure() { return scene.getObjectByName('clerk'); }

  function routeFrom(from, to) {
    const out = findPath(from, to);
    return out && out.length ? out : null;
  }

  function startLeading(place) {
    const figure = clerkFigure();
    if (!figure) return false;
    if (walk.mode === 'desk' || !walk.home) {
      walk.home = { x: figure.position.x, z: figure.position.z };
      walk.homeYaw = clerkYaw.rest === null ? figure.rotation.y : clerkYaw.rest;
    }
    // stop a little short of the bin, on the player's side, so the bin stays free to dig
    const from = walk.mode === 'desk' ? doorOut : { x: figure.position.x, z: figure.position.z };
    const path = routeFrom(from, { x: place.x, z: place.z });
    if (!path) return false;
    // stop short of the spot at the bin so he is beside you, not on top of you
    const last = path[path.length - 1], prev = path.length > 1 ? path[path.length - 2] : from;
    const gap = Math.hypot(last.x - prev.x, last.z - prev.z);
    if (gap > STAND_BACK + 0.3) path[path.length - 1] = { x: last.x + (prev.x - last.x) / gap * STAND_BACK, z: last.z + (prev.z - last.z) / gap * STAND_BACK };
    walk.legs = (walk.mode === 'desk' ? [{ x: walk.home.x, z: doorOut.z - 0.55 }, doorOut] : []).concat(path);
    walk.leg = 0; walk.mode = 'leading'; walk.pause = 0;
    clerkYaw.now = 0;
    return true;
  }

  function sendHome() {
    if (walk.mode === 'desk' || walk.mode === 'returning' || !walk.home) return;
    const figure = clerkFigure();
    if (!figure) return;
    const back = routeFrom({ x: figure.position.x, z: figure.position.z }, doorOut);
    walk.legs = (back || []).concat([doorOut, { x: walk.home.x, z: doorOut.z - 0.55 }, walk.home]);
    walk.leg = 0; walk.mode = 'returning';
  }

  // Moves the figure along walk.legs; returns true when the last leg is reached.
  function stepWalk(figure, dt, speed) {
    const leg = walk.legs[walk.leg];
    if (!leg) return true;
    const dx = leg.x - figure.position.x, dz = leg.z - figure.position.z, d = Math.hypot(dx, dz);
    if (d < 0.08) { walk.leg++; return walk.leg >= walk.legs.length; }
    const k = Math.min(1, speed * dt / d);
    figure.position.x += dx * k; figure.position.z += dz * k;
    let turn = Math.atan2(dx, dz) - figure.rotation.y;
    while (turn > Math.PI) turn -= Math.PI * 2;
    while (turn < -Math.PI) turn += Math.PI * 2;
    figure.rotation.y += turn * Math.min(1, dt * 6);
    walk.phase += dt;
    figure.position.y = Math.abs(Math.sin(walk.phase * 4.4)) * 0.03;
    return false;
  }

  function faceToward(figure, x, z, dt) {
    let turn = Math.atan2(x - figure.position.x, z - figure.position.z) - figure.rotation.y;
    while (turn > Math.PI) turn -= Math.PI * 2;
    while (turn < -Math.PI) turn += Math.PI * 2;
    figure.rotation.y += turn * Math.min(1, dt * 5);
  }

  function updateWalk(figure, dt, px, pz) {
    const dPlayer = Math.hypot(px - figure.position.x, pz - figure.position.z);
    if (walk.mode === 'leading') {
      if (walk.pause) {                                   // waiting for you to catch up
        figure.position.y = 0;
        faceToward(figure, px, pz, dt);
        if (dPlayer < RESUME_GAP) walk.pause = 0;
        return;
      }
      if (dPlayer > WAIT_GAP && walk.leg > 0) {
        walk.pause = 1; speak('Take your time — I’ll wait.', 2.5); return;
      }
      if (stepWalk(figure, dt, LEAD_SPEED)) {
        walk.mode = 'there'; walk.since = performance.now();
        figure.position.y = 0;
        speak(`Here you go — ${target ? target.label : 'this is the one'}.`, 4);
        sfx && sfx.play('blip', { volume: 0.7 });
      }
    } else if (walk.mode === 'there') {
      faceToward(figure, target ? target.rack.position.x : px, target ? target.rack.position.z : pz, dt);
    } else if (walk.mode === 'returning') {
      if (stepWalk(figure, dt, LEAD_SPEED + 0.2)) {
        walk.mode = 'desk';
        figure.position.set(walk.home.x, 0, walk.home.z);
        figure.rotation.y = walk.homeYaw;
        clerkYaw.now = 0;
      }
    }
  }

  // ------------------------------------------------------------------------- per frame
  const clerkYaw = { rest: null, now: null };

  function update(dt) {
    const px = camera.position.x, pz = camera.position.z;

    // the clerk looks up when you come near, and turns to watch you while you are close
    const figure = scene.getObjectByName('clerk');
    if (figure && walk.mode !== 'desk') updateWalk(figure, dt, px, pz);
    else if (figure) {
      if (clerkYaw.rest === null) { clerkYaw.rest = figure.rotation.y; clerkYaw.now = 0; }
      const dist = Math.hypot(px - figure.position.x, pz - figure.position.z);
      let goal = 0;                                   // turn away from rest, never an absolute yaw
      if (dist < LOOK_RANGE) {
        let off = Math.atan2(px - figure.position.x, pz - figure.position.z) - clerkYaw.rest;
        while (off > Math.PI) off -= Math.PI * 2;
        while (off < -Math.PI) off += Math.PI * 2;
        goal = Math.max(-1.9, Math.min(1.9, off));      // he cannot look through his own back
      }
      clerkYaw.now += (goal - clerkYaw.now) * Math.min(1, dt * 3.5);
      figure.rotation.y = clerkYaw.rest + clerkYaw.now;
    }

    // the first time you come up to the counter: a bell, and a word
    if (!welcomed && !isOpen && distToKiosk() < GREET_RANGE && !digging.held) {
      welcomed = true;
      speak('Welcome in. Press T if you want a steer.', 5);
      sfx && sfx.play('ding', { volume: 0.5 });
    }

    if (target) {
      const dx = target.x - px, dz = target.z - pz;
      const dist = Math.hypot(dx, dz);
      const yaw = player.yaw || 0;
      const right = dx * Math.cos(yaw) - dz * Math.sin(yaw);
      const ahead = -dx * Math.sin(yaw) - dz * Math.cos(yaw);
      arrow.firstChild.style.transform = `rotate(${Math.atan2(right, ahead)}rad)`;
      guideText.textContent = `${target.label} · ${dist < 10 ? dist.toFixed(1) : Math.round(dist)} m`;
      if (beacon) beacon.material.opacity = 0.62 + 0.2 * Math.sin(performance.now() / 380);
      if (dist < ARRIVE_RANGE || (digging.rack && digging.rack === target.rack)) {
        stats.arrived++;
        cancelGuide();
        speak('That’s the one. Happy digging.', 3.5);
      } else if (performance.now() - guideSince > 240000 || (walk.mode === 'there' && performance.now() - walk.since > 30000)) cancelGuide();
    }
  }

  return {
    update, open, close, guideTo, cancelGuide,
    isOpen: () => isOpen,
    ask: (text) => brain.ask(text),
    debug: () => ({
      open: isOpen, typing: revealing > 0, busy, guiding: !!target, target: target && { label: target.label, x: target.x, z: target.z },
      beacon: !!beacon, stats: { ...stats }, brain: brain.stats(), log: log.children.length,
      figureYaw: clerkYaw.now, restYaw: clerkYaw.rest,
      walk: { mode: walk.mode, leg: walk.leg, legs: walk.legs.length, paused: !!walk.pause, at: (() => { const f = clerkFigure(); return f && { x: f.position.x, z: f.position.z }; })() },
    }),
  };
}

export { createClerk };
